<script>
import { h } from 'vue';
import spellchecker from '@/utils/spellchecker';
import AppButton from './AppButton.vue';

export default {
    name: 'SpellCheckedText',
    components: { AppButton },
    props: {
        value: {
            type: String,
            default: ""
        }
    },
    data() {
        return {
            uid: Math.random().toString(36).substr(2, 9),
            manualCorrection: "",
            ignoredWords: new Set(),
            processedTree: [],
        };
    },
    watch: {
        value: {
            handler() {
                this.processContent();
            },
            immediate: true
        }
    },
    methods: {
        async tokenizeText(text) {
            if (!text) return [];
            const rawTokens = text.split(/(\s+|[.,/#!$%^&*;:{}=\-_`~()])/);
            const tokens = [];

            for (const t of rawTokens) {
                if (!t) continue;
                const isWord = /^[A-Za-z]+$/.test(t.trim());
                let correct = true;
                let suggestions = [];

                if (isWord) {
                    correct = await spellchecker.isCorrect(t.trim(), this.ignoredWords);
                    if (!correct) {
                        suggestions = await spellchecker.getSuggestions(t.trim());
                    }
                }

                tokens.push({
                    text: t,
                    isWord,
                    correct,
                    suggestions
                });
            }
            return tokens;
        },
        async parseHtmlToNodes(html) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(html || '', 'text/html');
            const nodes = Array.from(doc.body.childNodes);

            const mapNode = async (node) => {
                if (node.nodeType === Node.TEXT_NODE) {
                    const tokens = await this.tokenizeText(node.textContent);
                    return { type: 'text', tokens };
                } else if (node.nodeType === Node.ELEMENT_NODE) {
                    const children = [];
                    for (const child of Array.from(node.childNodes)) {
                        children.push(await mapNode(child));
                    }
                    const attrs = {};
                    for (const attr of Array.from(node.attributes)) {
                        attrs[attr.name] = attr.value;
                    }
                    return {
                        type: 'element',
                        tag: node.tagName.toLowerCase(),
                        attrs,
                        children
                    };
                }
                return null;
            };

            const result = [];
            for (const node of nodes) {
                const mapped = await mapNode(node);
                if (mapped) result.push(mapped);
            }
            return result;
        },
        async processContent() {
            this.$store.commit("SET_SPELL_CHECK_LOADING", true);
            try {
                this.processedTree = await this.parseHtmlToNodes(this.value);
            } finally {
                this.$store.commit("SET_SPELL_CHECK_LOADING", false);
            }
        },
        rebuildHtml(nodes) {
            let html = '';
            for (const node of nodes) {
                if (node.type === 'text') {
                    html += node.tokens.map(t => t.text).join("");
                } else if (node.type === 'element') {
                    const attrs = Object.entries(node.attrs)
                        .map(([k, v]) => ` ${k}="${v}"`)
                        .join("");
                    html += `<${node.tag}${attrs}>${this.rebuildHtml(node.children)}</${node.tag}>`;
                }
            }
            return html;
        },
        applyCorrection(tokens, tokenIndex, replacement) {
            if (!replacement) return;
            tokens[tokenIndex].text = replacement;

            const newValue = this.rebuildHtml(this.processedTree);
            this.$emit("input", newValue);
            this.manualCorrection = "";
        },
        ignoreWord(word) {
            const cleanWord = word.replace(/[.,/#!$%^&*;:{}=\-_`~()]/g, "").trim().toLowerCase();
            this.ignoredWords.add(cleanWord);
            this.processContent();
        }
    },

   render(h) {
    let globalWordId = 0;
    let globalNodeId = 0;

    const renderNodes = (nodes) => {
        return nodes
            .map((node) => {
                globalNodeId++;
                const currentNodeId = globalNodeId;

                if (node.type === "text") {
                    return node.tokens.map((token, tIndex) => {
                        if (token.isWord) {
                            globalWordId++;
                            const wordId = `word-${this.uid}-${globalWordId}`;
                            const isMisspelled =
                                !token.correct &&
                                !this.$store.state.loading.isExporting;

                            const popover =
                                isMisspelled &&
                                h(
                                    "b-popover",
                                    {
                                        props: {
                                            target: wordId,
                                            triggers: "hover",
                                            placement: "bottom",
                                            customClass: "spell-popover",
                                        },
                                    },
                                    [
                                        h(
                                            "template",
                                            { slot: "title" },
                                            "Suggestions"
                                        ),
                                        h("div", { class: "suggestions-list" }, [
                                            h(
                                                "div",
                                                { class: "suggestions-inner" },
                                                [
                                                    ...token.suggestions.map(
                                                        (sug) =>
                                                            h(
                                                                "div",
                                                                {
                                                                    key: sug,
                                                                    class:
                                                                        "suggestion-item",
                                                                    on: {
                                                                        click: () =>
                                                                            this.applyCorrection(
                                                                                node.tokens,
                                                                                tIndex,
                                                                                sug
                                                                            ),
                                                                    },
                                                                },
                                                                sug
                                                            )
                                                    ),
                                                    token.suggestions.length ===
                                                    0
                                                        ? h(
                                                              "div",
                                                              {
                                                                  class:
                                                                      "no-suggestions",
                                                              },
                                                              "No suggestions found"
                                                          )
                                                        : null,
                                                ]
                                            ),
                                            h("hr", { class: "my-2" }),
                                            h(
                                                "div",
                                                {
                                                    class:
                                                        "suggestion-item ignore-btn",
                                                    on: {
                                                        click: () =>
                                                            this.ignoreWord(
                                                                token.text
                                                            ),
                                                    },
                                                },
                                                `Ignore "${token.text}"`
                                            ),
                                            h("hr", { class: "my-2" }),
                                            h(
                                                "div",
                                                {
                                                    class:
                                                        "manual-fix d-flex gap-1",
                                                },
                                                [
                                                    h("input", {
                                                        attrs: {
                                                            type: "text",
                                                            placeholder:
                                                                "Manual fix...",
                                                        },
                                                        class:
                                                            "form-control form-control-sm",
                                                        domProps: {
                                                            value:
                                                                this.manualCorrection,
                                                        },
                                                        on: {
                                                            input: (e) =>
                                                                (this.manualCorrection =
                                                                    e.target.value),
                                                            keyup: (e) => {
                                                                if (
                                                                    e.key ===
                                                                    "Enter"
                                                                )
                                                                    this.applyCorrection(
                                                                        node.tokens,
                                                                        tIndex,
                                                                        this
                                                                            .manualCorrection
                                                                    );
                                                            },
                                                        },
                                                    }),
                                                    h(
                                                        "AppButton",
                                                        {
                                                            props: {
                                                                variant:
                                                                    "primary",
                                                                size: "sm",
                                                                disabled:
                                                                    this.manualCorrection.trim() ===
                                                                    "",
                                                            },
                                                            on: {
                                                                click: () =>
                                                                    this.applyCorrection(
                                                                        node.tokens,
                                                                        tIndex,
                                                                        this
                                                                            .manualCorrection
                                                                    ),
                                                            },
                                                        },
                                                        "✓"
                                                    ),
                                                ]
                                            ),
                                        ]),
                                    ]
                                );

                            return h(
                                "span",
                                {
                                    key: wordId,
                                    attrs: { id: wordId },
                                    class: {
                                        "token-word": true,
                                        misspelled: isMisspelled,
                                    },
                                },
                                [token.text, popover]
                            );
                        }

                        return h(
                            "span",
                            {
                                key: `s-${currentNodeId}-${tIndex}`,
                                class: "token-space",
                            },
                            token.text
                        );
                    });
                }

                if (node.type === "element") {
                    const data = {
                        key: `e-${currentNodeId}`,
                        attrs: { ...node.attrs },
                    };

                    if (node.attrs.class) {
                        data.class = node.attrs.class;
                        delete data.attrs.class;
                    }

                    return h(node.tag, data, renderNodes(node.children));
                }

                return null;
            })
            .flat(); // <-- CRITICAL FIX
    };

    return h(
        "span",
        { class: "spell-checked-container" },
        renderNodes(this.processedTree)
    );
}

};
</script>

<style lang="scss">
.spell-checked-container {
    display: inline;
    white-space: pre-wrap;

    ::v-deep p {
        margin-bottom: 0 !important;
        line-height: inherit !important;
        display: inline-block;
        width: 100%;
    }
	p:has(> br:only-child),
    p:empty {
      display: none;
}
	
}

.token-word {
    display: inline;
    position: relative;

    &.misspelled {
        text-decoration: underline wavy red;
        cursor: help;
    }
}

.spell-popover {
    .suggestions-list {
        min-width: 150px;
        padding: 5px;

        .suggestions-inner {
            max-height: 200px;
            overflow-y: auto;
            scrollbar-width: thin;

            &::-webkit-scrollbar {
                width: 4px;
            }

            &::-webkit-scrollbar-track {
                background: #f1f1f1;
            }

            &::-webkit-scrollbar-thumb {
                background: #888;
                border-radius: 4px;
            }

            &::-webkit-scrollbar-thumb:hover {
                background: #555;
            }
        }

        .suggestion-item {
            padding: 4px 8px;
            cursor: pointer;
            border-radius: 4px;
            font-weight: 500;
            color: #4475b4;

            &:hover {
                background: #f0f4f8;
            }

            &.ignore-btn {
                color: #666;
                font-size: 13px;
                font-style: italic;
            }
        }

        .no-suggestions {
            padding: 4px 8px;
            font-size: 12px;
            color: #999;
            font-style: italic;
        }

        .manual-fix {
            display: flex;
            align-items: center;
            gap: 5px;

            input {
                flex: 1;
            }
        }
    }
 	
}
</style>
